import { useState, useEffect, useRef, useCallback } from "react";
import {
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  LinearProgress,
  Snackbar,
  Alert,
  Stack,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  styled,
  Collapse,
  Chip,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
  useTheme,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorIcon from "@mui/icons-material/Error";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import {
  getUploadExcelSettings,
  setUploadExcelSettings,
  getCustomFileNameForTitle,
  setCustomFileNameForTitle
} from "./uploadExcelSettings";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";

// Re-export for callers that want a single import surface for upload/export UI.
export { exportDisclosuresToExcel } from "../../Console-Panel/Disclosure/DisclosureExcelUpdate";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 4px 20px rgba(96, 63, 151, 0.15)",
    maxWidth: 600,
    width: "100%",
    margin: 16,
    background: theme.palette.background.paper
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontWeight: 600
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "24px",
  background: theme.palette.background.paper
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "16px 24px",
  borderTop: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  border: "1px dashed",
  borderColor: theme.palette.divider,
  borderRadius: 8,
  backgroundColor: theme.palette.background.default
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  padding: "12px 16px",
  borderRadius: 8,
  fontWeight: 500,
  boxShadow: "none",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(96, 63, 151, 0.15)"
  }
}));

const FileDisplayPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  border: "1px solid",
  borderColor: theme.palette.divider,
  borderRadius: 8,
  backgroundColor: theme.palette.background.default
}));

const FILE_INPUT_ID = "fileInput";

function formatFileSize(bytes) {
  if (bytes == null || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function parseErrorReportText(text) {
  const details = [];
  const lines = (text || "").trim().split(/\r?\n/);
  lines.forEach((line) => {
    const rowMatch = line.match(/Row\s+(\d+):\s*(.*)/i);
    if (rowMatch) {
      const row = rowMatch[1];
      const rest = rowMatch[2].trim();
      rest.split(/\s*\|\s*/).forEach((msg) => {
        const trimmed = msg.trim();
        if (trimmed) {
          const colMatch = trimmed.match(/Column\s+(\d+)\s+(.*)/i);
          details.push({
            row,
            column: colMatch ? colMatch[1] : "-",
            message: colMatch ? colMatch[2].trim() : trimmed
          });
        }
      });
    }
  });
  return details;
}

const SlideTransition = (props) => <Slide {...props} direction='down' />;

const UploadExcelDialogEnhanced = ({
  title,
  open,
  onClose,
  onDownloadTemplate,
  onFileChange,
  onUpload,
  onExportExisting,
  exportExistingLabel = "Export Excel",
  selectedFile,
  uploading,
  processing = false,
  infoTitle,
  infoMessage,
  alertMessage,
  alertSeverity,
  onCloseAlert,
  onReset,
  errorFileUrl,
  validRows,
  templateHeadersString = "",
  errorSummary,
  progressValue,
  templateInstructions = [],
  fixedValueGroups = []
}) => {
  const theme = useTheme();
  const dialogContentRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [includeInstructions, setIncludeInstructions] = useState(true);
  const [includeSampleData, setIncludeSampleData] = useState(true);
  const [customFileName, setCustomFileName] = useState("");
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDetails, setErrorDetails] = useState([]);
  const [resolvedErrorIndices, setResolvedErrorIndices] = useState(
    () => new Set()
  );
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [fixedValuesDialogOpen, setFixedValuesDialogOpen] = useState(false);
  const [fixedValuesTabIndex, setFixedValuesTabIndex] = useState(0);
  const isBusy = uploading || processing;
  const hasNoValidRows = !!selectedFile && !isBusy && validRows === 0;
  const showValidationIssue =
    !!selectedFile && !isBusy && (hasNoValidRows || !!errorFileUrl);
  const validationColor = "error";
  const fileStatusColor =
    isBusy || validRows == null
      ? "info"
      : errorFileUrl
        ? validRows > 0
          ? "warning"
          : "error"
        : validRows > 0
          ? "success"
          : "error";

  const handleChooseFile = () => {
    if (isBusy) return;
    const input = document.getElementById(FILE_INPUT_ID);
    if (input) {
      input.value = "";
      input.click();
    }
  };

  useEffect(() => {
    const s = getUploadExcelSettings();
    setIncludeInstructions(s.includeInstructions);
    setIncludeSampleData(s.includeSampleData !== false);
    setCustomFileName(getCustomFileNameForTitle(title));
  }, [open, title]);

  const scrollDialogToBottom = useCallback(() => {
    const el = dialogContentRef.current;
    if (!el) return;
    // Let the DOM paint progress/errors first.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } catch (_) {}
      });
    });
  }, []);

  const handleUploadClick = () => {
    scrollDialogToBottom();
    onUpload?.();
    // Progress UI often renders after parent state flips.
    setTimeout(scrollDialogToBottom, 0);
  };

  useEffect(() => {
    if (uploading || processing) scrollDialogToBottom();
  }, [uploading, processing, scrollDialogToBottom]);

  useEffect(() => {
    if (selectedFile && !isBusy) scrollDialogToBottom();
  }, [selectedFile, validRows, errorFileUrl, isBusy, scrollDialogToBottom]);

  const persistIncludeInstructions = (value) => {
    setIncludeInstructions(value);
    setUploadExcelSettings({ includeInstructions: value });
  };

  const persistIncludeSampleData = (value) => {
    setIncludeSampleData(value);
    setUploadExcelSettings({ includeSampleData: value });
  };

  const persistCustomFileName = (value) => {
    setCustomFileName(value);
    setCustomFileNameForTitle(title, value);
  };

  const handleDownloadTemplate = () => {
    onDownloadTemplate({
      includeInstructions,
      includeSampleData,
      customFileName: customFileName.trim() || undefined
    });
  };

  const handleCopyHeaders = async () => {
    if (!templateHeadersString) return;
    try {
      await navigator.clipboard.writeText(templateHeadersString);
      setCopySnackbarMessage(
        "Headers copied to clipboard! Paste into Excel as the first row."
      );
      setCopySnackbarOpen(true);
    } catch {
      setCopySnackbarOpen(false);
    }
  };

  const handleCopyFixedValues = async (values) => {
    const text = values
      .map((v) => (v == null || v === "" ? "" : String(v)))
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopySnackbarMessage("Fixed values copied to clipboard.");
      setCopySnackbarOpen(true);
    } catch (_) {}
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isBusy) return;
    setDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (isBusy) return;
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    const file = files[0];
    const name = file?.name?.toLowerCase() || "";
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) return;
    const syntheticEvent = { target: { files: [file], value: "" } };
    onFileChange(syntheticEvent);
  };

  const handleViewErrors = async () => {
    if (!errorFileUrl) return;
    try {
      const res = await fetch(errorFileUrl);
      const text = await res.text();
      setErrorDetails(parseErrorReportText(text));
      setResolvedErrorIndices(new Set());
      setErrorDialogOpen(true);
    } catch {
      setErrorDetails([]);
      setResolvedErrorIndices(new Set());
      setErrorDialogOpen(true);
    }
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
  };

  const toggleErrorResolved = (index) => {
    setResolvedErrorIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const markAllErrorsResolved = () => {
    setResolvedErrorIndices(new Set(errorDetails.map((_, i) => i)));
  };

  const clearAllResolvedMarks = () => {
    setResolvedErrorIndices(new Set());
  };

  const handleCopyErrorSummary = async () => {
    const resolved = resolvedErrorIndices.size;
    const total = errorDetails.length;
    const remaining = total - resolved;
    const lines = [
      `Validation errors summary`,
      `Total: ${total} | Marked as fixed: ${resolved} | Remaining: ${remaining}`,
      ``,
      remaining > 0 ? `Remaining errors:` : `All items marked as fixed.`,
      ...errorDetails
        .filter((_, i) => !resolvedErrorIndices.has(i))
        .map((e) => `  Row ${e.row}, Col ${e.column}: ${e.message}`)
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopySnackbarMessage("Error summary copied to clipboard.");
      setCopySnackbarOpen(true);
    } catch (_) {}
  };

  const handleDownloadErrorReport = () => {
    if (!errorFileUrl) return;
    const a = document.createElement("a");
    a.href = errorFileUrl;
    a.download = "validation_errors.txt";
    a.click();
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={processing ? undefined : onClose}
        fullWidth
      >
        <StyledDialogTitle>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                backgroundColor: "primary.lighter",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <UploadFileIcon />
            </Box>
            <Box>
              <Typography
                variant='h6'
                fontWeight={600}
                sx={{ lineHeight: 1.3 }}
              >
                Upload Excel Data
              </Typography>
              <Typography variant='caption'>
                Import your data using our template in Excel format
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label='close dialog'
            onClick={onClose}
            disabled={processing}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "white"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent ref={dialogContentRef} sx={{ pt: 1, pb: 2 }}>
          <br />
          <Stack spacing={3}>
            {/* Template Section - Enhanced */}
            <StyledPaper
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden"
              }}
            >
              <Stack spacing={2} sx={{ p: 2.5 }}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Stack>
                    <Typography
                      variant='subtitle1'
                      fontWeight={500}
                      gutterBottom
                    >
                      Template Options
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Download our template or copy headers to create your file
                    </Typography>
                  </Stack>
                  <Tooltip
                    title={
                      showSettings ? "Hide settings" : "Show template settings"
                    }
                  >
                    <IconButton
                      size='small'
                      onClick={() => setShowSettings((v) => !v)}
                      aria-expanded={showSettings}
                      aria-controls='template-settings'
                      sx={{
                        transition: "transform 0.2s",
                        transform: showSettings ? "rotate(180deg)" : "none"
                      }}
                    >
                      <SettingsIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Collapse
                  in={showSettings}
                  id='template-settings'
                  unmountOnExit
                >
                  <Stack
                    spacing={1.5}
                    sx={{
                      mb: 1.5,
                      pt: 1,
                      borderTop: 1,
                      borderColor: "divider"
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeInstructions}
                          onChange={(e) =>
                            persistIncludeInstructions(e.target.checked)
                          }
                          size='small'
                          sx={{
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" }
                          }}
                        />
                      }
                      label={
                        <Typography variant='body2'>
                          Include instructions sheet in template
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeSampleData}
                          onChange={(e) =>
                            persistIncludeSampleData(e.target.checked)
                          }
                          size='small'
                          sx={{
                            color: "primary.main",
                            "&.Mui-checked": { color: "primary.main" }
                          }}
                        />
                      }
                      label={
                        <Typography variant='body2'>
                          Include sample data rows in template
                        </Typography>
                      }
                    />
                    <TextField
                      size='small'
                      fullWidth
                      label='Custom file name (optional)'
                      placeholder='e.g., inventory_template.xlsx'
                      value={customFileName}
                      onChange={(e) => persistCustomFileName(e.target.value)}
                      inputProps={{ "aria-label": "custom template file name" }}
                      helperText='Leave blank to use default name'
                      variant='outlined'
                    />
                  </Stack>
                </Collapse>

                {/* Core actions – full width */}
                <Stack spacing={1.5}>
                  {!!onExportExisting && (
                    <Tooltip
                      title={
                        !processing
                          ? "Export your existing items to an Excel file"
                          : "Export disabled during processing"
                      }
                      placement='top'
                    >
                      <span style={{ width: "100%" }}>
                        <StyledButton
                          variant='outlined'
                          startIcon={<FileDownloadIcon />}
                          onClick={onExportExisting}
                          disabled={processing}
                          fullWidth
                          sx={{
                            borderColor: "primary.main",
                            color: "primary.main",
                            py: 1.1,
                            fontWeight: 500,
                            "&:hover": {
                              borderColor: "primary.dark",
                              backgroundColor: "primary.lighter",
                              color: "primary.dark"
                            }
                          }}
                        >
                          {exportExistingLabel}
                        </StyledButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={
                      !processing
                        ? "Download Excel template with formatting and validation rules"
                        : "Template download disabled during processing"
                    }
                    placement='top'
                  >
                    <span style={{ width: "100%" }}>
                      <StyledButton
                        variant='contained'
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        disabled={processing}
                        fullWidth
                        sx={{
                          backgroundColor: "primary.main",
                          color: "primary.contrastText",
                          py: 1.2,
                          fontWeight: 500,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)"
                          },
                          "&:disabled": {
                            backgroundColor: "action.disabledBackground",
                            color: "text.disabled"
                          }
                        }}
                      >
                        Download Template
                      </StyledButton>
                    </span>
                  </Tooltip>

                  {templateHeadersString && (
                    <Tooltip
                      title={
                        !processing
                          ? "Copy column headers to clipboard. Paste as first row in Excel."
                          : "Copy disabled during processing"
                      }
                      placement='bottom'
                    >
                      <span style={{ width: "100%" }}>
                        <StyledButton
                          variant='outlined'
                          startIcon={<ContentCopyIcon />}
                          onClick={handleCopyHeaders}
                          disabled={processing}
                          fullWidth
                          sx={{
                            borderColor: "primary.main",
                            color: "primary.main",
                            py: 1.1,
                            fontWeight: 500,
                            "&:hover": {
                              borderColor: "primary.dark",
                              backgroundColor: "primary.lighter",
                              color: "primary.dark"
                            }
                          }}
                        >
                          Copy Column Headers
                        </StyledButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Auxiliary reference – compact, secondary */}
                  {(templateInstructions?.length > 0 ||
                    (fixedValueGroups?.length ?? 0) > 0) && (
                    <Box sx={{ pt: 0.5 }}>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                          display: "block",
                          mb: 1,
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em"
                        }}
                      >
                        Reference
                      </Typography>
                      <Stack
                        direction='row'
                        spacing={1}
                        flexWrap='wrap'
                        sx={{
                          "& > .MuiButton-root": {
                            py: 0.75,
                            px: 1.5,
                            minHeight: 36,
                            fontSize: "0.8125rem",
                            textTransform: "none",
                            borderColor: "divider",
                            color: "text.secondary",
                            "&:hover": {
                              borderColor: "text.secondary",
                              backgroundColor: "action.hover",
                              color: "text.primary"
                            }
                          }
                        }}
                      >
                        {templateInstructions &&
                          Array.isArray(templateInstructions) &&
                          templateInstructions.length > 0 && (
                            <Button
                              variant='outlined'
                              size='small'
                              startIcon={<InfoIcon sx={{ fontSize: 18 }} />}
                              onClick={() => setInstructionsDialogOpen(true)}
                              disabled={processing}
                            >
                              View instructions
                            </Button>
                          )}
                        {fixedValueGroups &&
                          Array.isArray(fixedValueGroups) &&
                          fixedValueGroups.length > 0 && (
                            <Tooltip title='Reference allowed fixed values for this upload.'>
                              <Button
                                variant='outlined'
                                size='small'
                                startIcon={
                                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                                }
                                onClick={() => {
                                  setFixedValuesTabIndex(0);
                                  setFixedValuesDialogOpen(true);
                                }}
                                disabled={processing}
                              >
                                Fixed values
                              </Button>
                            </Tooltip>
                          )}
                      </Stack>
                    </Box>
                  )}
                </Stack>

                {!!infoMessage && (
                  <Alert severity='info' variant='outlined'>
                    {infoTitle && (
                      <Typography
                        variant='subtitle2'
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {infoTitle}
                      </Typography>
                    )}
                    {infoMessage}
                  </Alert>
                )}

                <Box sx={{ pt: 1 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                    sx={{ mb: 0.5 }}
                  >
                    Accepted formats: .xlsx, .xls • Max size: 25MB
                  </Typography>
                </Box>
              </Stack>
            </StyledPaper>

            <input
              type='file'
              id={FILE_INPUT_ID}
              accept='.xlsx,.xls'
              hidden
              onChange={onFileChange}
              disabled={isBusy}
              aria-label='File upload input'
            />

            {/* Enhanced Drag & Drop Zone */}
            {!selectedFile && (
              <StyledPaper
                elevation={0}
                role='button'
                tabIndex={isBusy ? -1 : 0}
                aria-label='Choose an Excel file'
                aria-disabled={isBusy}
                sx={{
                  border: "2px dashed",
                  borderColor: dragActive ? "primary.main" : "divider",
                  borderRadius: 2,
                  p: { xs: 2, sm: 3 },
                  transition: "all 0.25s ease",
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2
                  },
                  backgroundColor: dragActive
                    ? "primary.lightest"
                    : "background.paper",
                  cursor: processing ? "not-allowed" : "pointer",
                  "&:hover": {
                    borderColor: processing ? "divider" : "primary.main",
                    backgroundColor: processing
                      ? "background.paper"
                      : "primary.lightest"
                  }
                }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleChooseFile}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleChooseFile();
                  }
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems='center'
                  justifyContent='center'
                  sx={{ minHeight: { xs: 140, sm: 100 } }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      backgroundColor: dragActive
                        ? "primary.lighter"
                        : "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s"
                    }}
                  >
                    {dragActive ? (
                      <CloudUploadIcon
                        sx={{ color: "primary.main", fontSize: 32 }}
                      />
                    ) : (
                      <UploadFileIcon
                        sx={{ color: "text.secondary", fontSize: 32 }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                      maxWidth: 400
                    }}
                  >
                    <Typography variant='h6' fontWeight={500} gutterBottom>
                      {dragActive
                        ? "Drop your file here"
                        : "Drag & drop your Excel file"}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      gutterBottom
                    >
                      or{" "}
                      <Box
                        component='span'
                        sx={{
                          color: "primary.main",
                          fontWeight: 500,
                          cursor: processing ? "not-allowed" : "pointer",
                          "&:hover": {
                            textDecoration: processing ? "none" : "underline"
                          }
                        }}
                      >
                        browse files
                      </Box>
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      display='block'
                      sx={{ mt: 0.5 }}
                    >
                      Files must follow the template structure for successful
                      import
                    </Typography>
                  </Box>
                </Stack>
              </StyledPaper>
            )}

            {/* Enhanced File Display */}
            {selectedFile && (
              <FileDisplayPaper
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: `${fileStatusColor}.light`,
                  borderRadius: 2,
                  backgroundColor: alpha(
                    theme.palette[fileStatusColor].main,
                    0.04
                  )
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems='flex-start'
                  sx={{ p: 2 }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      backgroundColor: alpha(
                        theme.palette[fileStatusColor].main,
                        0.1
                      ),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0
                    }}
                  >
                    <InsertDriveFileIcon
                      sx={{ color: `${fileStatusColor}.main`, fontSize: 28 }}
                    />
                  </Box>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      gutterBottom
                      sx={{ fontWeight: 500 }}
                    >
                      Selected File
                    </Typography>
                    <Stack
                      direction='row'
                      alignItems='center'
                      spacing={1}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography
                        component='span'
                        title={selectedFile.name}
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {selectedFile.name}
                      </Typography>
                      <Tooltip
                        title={`File size: ${formatFileSize(selectedFile.size)}`}
                      >
                        <Chip
                          label={formatFileSize(selectedFile.size)}
                          size='small'
                          sx={{
                            height: 20,
                            fontSize: "0.75rem",
                            backgroundColor: alpha(
                              theme.palette.success.light,
                              0.5
                            ),
                            color: "success.dark"
                          }}
                        />
                      </Tooltip>
                    </Stack>

                    <Stack
                      direction='row'
                      spacing={1.5}
                      flexWrap='wrap'
                      sx={{ mt: 0.5 }}
                    >
                      <Typography variant='caption' color='text.secondary'>
                        Last modified:{" "}
                        {new Date(
                          selectedFile.lastModified
                        ).toLocaleDateString()}
                      </Typography>
                      {!isBusy && validRows != null && (
                        <Chip
                          size='small'
                          color={validRows > 0 ? "success" : "error"}
                          label={`${validRows} valid row${validRows !== 1 ? "s" : ""}`}
                        />
                      )}
                    </Stack>
                  </Box>

                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{ mt: { xs: 1, sm: 0 } }}
                  >
                    <Tooltip title='Download file'>
                      <IconButton
                        component='a'
                        href={URL.createObjectURL(selectedFile)}
                        download={selectedFile.name}
                        size='small'
                        sx={{
                          color: "success.main",
                          backgroundColor: "success.lighter",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.success.light,
                              0.5
                            )
                          }
                        }}
                      >
                        <FileDownloadIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Remove file'>
                      <IconButton
                        onClick={() => {
                          const el = document.getElementById(FILE_INPUT_ID);
                          if (el) el.value = "";
                          onReset();
                        }}
                        disabled={isBusy}
                        size='small'
                        sx={{
                          color: "error.main",
                          backgroundColor: "error.lighter",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.error.light,
                              0.5
                            )
                          }
                        }}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </FileDisplayPaper>
            )}

            {/* Enhanced Progress Indicators */}
            {(uploading || processing) && (
              <Box sx={{ width: "100%", mt: 1 }}>
                <LinearProgress
                  variant={
                    Number.isFinite(progressValue)
                      ? "determinate"
                      : "indeterminate"
                  }
                  value={progressValue || 0}
                  sx={{
                    borderRadius: 2,
                    height: 8,
                    backgroundColor: "primary.lighter",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "primary.main",
                      borderRadius: 2
                    }
                  }}
                />
                <Stack
                  direction='row'
                  justifyContent='space-between'
                  sx={{ mt: 1 }}
                >
                  <Typography
                    variant='body2'
                    fontWeight={500}
                    color='text.primary'
                  >
                    {uploading ? "Uploading file..." : "Processing data..."}
                  </Typography>
                  {Number.isFinite(progressValue) && (
                    <Typography variant='body2' color='text.secondary'>
                      {Math.round(progressValue)}%
                    </Typography>
                  )}
                </Stack>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {uploading
                    ? "Securely transferring your file to our servers"
                    : "Validating and importing your data. This may take a moment for large files."}
                </Typography>
                {!uploading && validRows > 0 && (
                  <Typography
                    variant='caption'
                    color='success.main'
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    ✓ {validRows} rows validated successfully
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </StyledDialogContent>

        <StyledDialogActions
          sx={{
            p: 2.5,
            pt: 1.5,
            borderTop: 1,
            borderColor: "divider",
            flexDirection: "column",
            gap: 1.5,
            "& > :not(:first-of-type)": { ml: 0 }
          }}
        >
          {showValidationIssue && (
            <Alert
              severity={validationColor}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                py: 1.5,
                "& .MuiAlert-message": { width: "100%" }
              }}
            >
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                {hasNoValidRows
                  ? "No valid rows — upload unavailable"
                  : "Validation errors found"}
              </Typography>
              <Typography variant='body2'>
                {errorSummary ||
                  (hasNoValidRows
                    ? "Check that your file contains data rows and matches the template, then select the corrected file."
                    : `${validRows} valid row${validRows !== 1 ? "s" : ""} can still be uploaded. Review the errors for the remaining rows.`)}
              </Typography>
              {!hasNoValidRows && errorSummary && (
                <Typography variant='body2' sx={{ mt: 0.5 }}>
                  {validRows} valid row{validRows !== 1 ? "s" : ""} can still be
                  uploaded.
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  mt: 2
                }}
              >
                {" "}
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent={{ xs: "space-between", sm: "flex-end" }}
                  spacing={1}
                >
                  {errorFileUrl && (
                    <Button
                      size='small'
                      variant='contained'
                      color={validationColor}
                      startIcon={<ErrorIcon />}
                      onClick={handleViewErrors}
                      sx={{
                        minHeight: 36,
                        px: 2,
                        boxShadow: "none",
                        alignSelf: { xs: "stretch", sm: "center" },
                        bgcolor: alpha(theme.palette.error.main, 0.9),
                        "&:hover": {
                          bgcolor: theme.palette.error.main
                        }
                      }}
                    >
                      View errors
                    </Button>
                  )}

                  {errorFileUrl && (
                    <IconButtonWithTooltip
                      icon={DownloadIcon}
                      tooltipTitle='Download error report'
                      onClick={handleDownloadErrorReport}
                      color={validationColor}
                      size='small'
                      sx={{
                        width: 36,
                        height: 36,
                        border: "1px solid",
                        borderColor: `${validationColor}.main`,
                        borderRadius: 1.5,
                        backgroundColor: alpha(
                          theme.palette[validationColor].main,
                          0.06
                        ),
                        "&:hover": {
                          backgroundColor: `${validationColor}.main`,
                          color: `${validationColor}.contrastText`
                        }
                      }}
                    />
                  )}
                </Stack>
                <Button
                  size='small'
                  variant={errorFileUrl ? "outlined" : "contained"}
                  startIcon={<UploadFileIcon />}
                  onClick={() => {
                    onReset();
                    handleChooseFile();
                  }}
                  sx={{
                    minHeight: 36,
                    px: 2,
                    boxShadow: "none",
                    color: "white",
                    borderColor: "info.main",
                    bgcolor: alpha(theme.palette.info.main, 0.9),
                    "&:hover": {
                      bgcolor: theme.palette.info.main,
                      borderColor: "info.main"
                    }
                  }}
                >
                  Select corrected file
                </Button>
              </Box>
            </Alert>
          )}
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.5}
            sx={{ width: "100%" }}
          >
            <Button
              onClick={onClose}
              disabled={processing}
              fullWidth
              sx={{
                py: 1.1,
                color: processing ? "text.disabled" : "text.secondary",
                borderColor: processing ? "divider" : "primary.main",
                "&:hover": {
                  backgroundColor: processing ? "transparent" : "action.hover",
                  borderColor: processing ? "divider" : "primary.dark"
                }
              }}
              variant='outlined'
            >
              {processing ? "Processing..." : "Cancel"}
            </Button>

            <Tooltip
              title={
                !selectedFile
                  ? "Select a file to upload"
                  : uploading || processing
                    ? "Upload in progress"
                    : validRows === 0
                      ? "No valid rows found in file"
                      : ""
              }
              placement='top'
              disableHoverListener={
                !!(
                  selectedFile &&
                  !uploading &&
                  !processing &&
                  validRows &&
                  validRows > 0
                )
              }
            >
              <span style={{ width: "100%" }}>
                <Button
                  onClick={handleUploadClick}
                  variant='contained'
                  disabled={
                    !selectedFile ||
                    uploading ||
                    processing ||
                    !validRows ||
                    validRows <= 0
                  }
                  fullWidth
                  sx={{
                    py: 1.2,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 500,
                    boxShadow: "0 3px 8px rgba(25, 118, 210, 0.2)",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)"
                    },
                    "&:disabled": {
                      backgroundColor: "action.disabledBackground",
                      color: "text.disabled",
                      boxShadow: "none"
                    }
                  }}
                  startIcon={
                    processing ? (
                      <CircularProgress
                        size={20}
                        sx={{ color: "primary.contrastText" }}
                      />
                    ) : null
                  }
                >
                  {processing
                    ? "Processing..."
                    : uploading
                      ? "Uploading..."
                      : "Upload Data"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </StyledDialogActions>
      </StyledDialog>

      {/* Enhanced Snackbars */}
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={alertSeverity === "error" ? 6000 : 4000}
        onClose={onCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={onCloseAlert}
          severity={alertSeverity}
          variant='filled'
          iconMapping={{
            success: <CheckCircleIcon fontSize='inherit' />,
            error: <ErrorIcon fontSize='inherit' />,
            warning: <WarningIcon fontSize='inherit' />,
            info: <InfoIcon fontSize='inherit' />
          }}
          sx={{
            minWidth: { xs: "90%", sm: 360 },
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 500
          }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2500}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity='success'
          variant='filled'
          icon={<ContentCopyIcon fontSize='inherit' />}
          sx={{
            minWidth: { xs: "90%", sm: 320 },
            borderRadius: 2,
            backgroundColor: "success.dark",
            fontWeight: 500
          }}
          onClose={() => setCopySnackbarOpen(false)}
        >
          {copySnackbarMessage}
        </Alert>
      </Snackbar>

      {/* Error Details Dialog – tooled with summary & fixed tracking */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden" }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pb: 1.5
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='wrap'
            spacing={1.5}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <ErrorIcon color='error' />
              <Typography variant='h6'>Validation Errors</Typography>
            </Stack>
            <Chip
              size='small'
              label={`${errorDetails.length} error${errorDetails.length !== 1 ? "s" : ""}`}
              color='error'
              variant='outlined'
            />
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <br />
          <Alert severity='warning' sx={{ mb: 2 }}>
            Correct these in your Excel file and re-upload. You can mark items
            as fixed as you go.
            <br />
            Note: Marking errors as fixed is for your personal tracking only and
            will not be saved or submitted.
          </Alert>

          {/* Summary & toolbar */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent='space-between'
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Stack
              direction='row'
              alignItems='center'
              spacing={1.5}
              flexWrap='wrap'
            >
              <Typography variant='body2' color='text.secondary'>
                Marked as fixed:{" "}
                <Typography
                  component='span'
                  variant='body2'
                  fontWeight={600}
                  color='success.main'
                >
                  {resolvedErrorIndices.size}
                </Typography>
                {" / "}
                <Typography component='span' variant='body2' fontWeight={600}>
                  {errorDetails.length}
                </Typography>
              </Typography>
              {errorDetails.length > 0 && (
                <Chip
                  size='small'
                  icon={
                    resolvedErrorIndices.size === errorDetails.length ? (
                      <CheckCircleIcon />
                    ) : null
                  }
                  label={
                    resolvedErrorIndices.size === errorDetails.length
                      ? "All marked fixed"
                      : `${errorDetails.length - resolvedErrorIndices.size} remaining`
                  }
                  color={
                    resolvedErrorIndices.size === errorDetails.length
                      ? "success"
                      : "default"
                  }
                  variant='outlined'
                />
              )}
            </Stack>
            <Stack direction='row' spacing={1} flexWrap='wrap'>
              <Button
                size='small'
                variant='outlined'
                onClick={markAllErrorsResolved}
                disabled={errorDetails.length === 0}
              >
                Mark all fixed
              </Button>
              <Button
                size='small'
                variant='outlined'
                onClick={clearAllResolvedMarks}
                disabled={resolvedErrorIndices.size === 0}
              >
                Clear marks
              </Button>
              <Button
                size='small'
                variant='outlined'
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyErrorSummary}
                disabled={errorDetails.length === 0}
              >
                Copy summary
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              maxHeight: 400,
              overflow: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "background.paper"
            }}
          >
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding='checkbox'
                    sx={{ width: 56, fontWeight: 600 }}
                  >
                    Fixed
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Column</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorDetails.map((error, index) => {
                  const resolved = resolvedErrorIndices.has(index);
                  return (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        backgroundColor: resolved
                          ? alpha(theme.palette.success.main, 0.06)
                          : undefined,
                        "& td": resolved
                          ? {
                              textDecoration: "line-through",
                              color: "text.secondary"
                            }
                          : undefined
                      }}
                    >
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={resolved}
                          onChange={() => toggleErrorResolved(index)}
                          size='small'
                          color='success'
                          inputProps={{
                            "aria-label": `Mark row ${error.row} as fixed`
                          }}
                        />
                      </TableCell>
                      <TableCell>{error.row}</TableCell>
                      <TableCell>
                        <Chip
                          label={error.column}
                          size='small'
                          color={resolved ? "default" : "error"}
                          variant='outlined'
                        />
                      </TableCell>
                      <TableCell>{error.message}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ borderTop: 1, borderColor: "divider", px: 2, py: 1.5 }}
        >
          <Button onClick={handleCloseErrorDialog}>Close</Button>
          <Button
            onClick={handleDownloadErrorReport}
            variant='contained'
            startIcon={<DownloadIcon />}
            color='error'
          >
            Download full report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template instructions viewer */}
      <Dialog
        open={instructionsDialogOpen}
        onClose={() => setInstructionsDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", pb: 1.5 }}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <InfoIcon color='info' />
            <Typography variant='h6'>Template instructions</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <br />
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Use this as reference when filling the template. Same content as the
            Instructions sheet in the downloaded file.
          </Typography>
          <Box
            sx={{
              maxHeight: 360,
              overflow: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "background.default"
            }}
          >
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  {((templateInstructions || [])[0] || []).map((cell, cIdx) => (
                    <TableCell
                      key={cIdx}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "grey.100",
                        whiteSpace: "pre-wrap"
                      }}
                    >
                      {cell != null ? String(cell) : ""}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(templateInstructions || []).slice(1).map((row, rIdx) => {
                  const headerLen = ((templateInstructions || [])[0] || [])
                    .length;
                  const cells = Array.isArray(row)
                    ? row.slice(0, headerLen)
                    : [];
                  while (cells.length < headerLen) cells.push("");
                  return (
                    <TableRow key={rIdx}>
                      {cells.map((cell, cIdx) => (
                        <TableCell
                          key={cIdx}
                          sx={{
                            whiteSpace: "pre-wrap",
                            verticalAlign: "top"
                          }}
                        >
                          {cell != null ? String(cell) : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: "divider" }}>
          <Button onClick={() => setInstructionsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fixed values dialog – tabs per group, copy per value or copy all */}
      <Dialog
        open={fixedValuesDialogOpen}
        onClose={() => setFixedValuesDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <ContentCopyIcon color='secondary' />
            <Typography variant='h6'>Fixed values</Typography>
          </Stack>
        </DialogTitle>
        <Tabs
          value={Math.max(
            0,
            Math.min(fixedValuesTabIndex, (fixedValueGroups || []).length - 1)
          )}
          onChange={(_, v) => setFixedValuesTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
        >
          {(fixedValueGroups || []).map((group, i) => (
            <Tab key={i} label={group.label} id={`fixed-values-tab-${i}`} />
          ))}
        </Tabs>
        <DialogContent sx={{ pt: 2, minHeight: 280 }}>
          {(fixedValueGroups || []).map((group, groupIndex) => {
            const rawValues = Array.isArray(group.values) ? group.values : [];
            const isActive = fixedValuesTabIndex === groupIndex;
            return (
              <Box
                key={groupIndex}
                role='tabpanel'
                id={`fixed-values-tabpanel-${groupIndex}`}
                aria-labelledby={`fixed-values-tab-${groupIndex}`}
                hidden={!isActive}
                sx={{ display: isActive ? "block" : "none" }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                  >
                    <Typography variant='body2' color='text.secondary'>
                      Copy a value or all values for this field.
                    </Typography>
                    <Button
                      size='small'
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyFixedValues(group.values || [])}
                      disabled={rawValues.length === 0}
                    >
                      Copy all
                    </Button>
                  </Stack>
                  <List
                    dense
                    disablePadding
                    sx={{ bgcolor: "background.default", borderRadius: 1 }}
                  >
                    {rawValues.map((val, idx) => {
                      const display =
                        val === "" || val == null ? "(blank)" : String(val);
                      return (
                        <ListItem
                          key={idx}
                          secondaryAction={
                            <IconButton
                              edge='end'
                              size='small'
                              onClick={() =>
                                handleCopyFixedValues([val == null ? "" : val])
                              }
                              aria-label={`Copy ${display}`}
                            >
                              <ContentCopyIcon fontSize='small' />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={display}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx:
                                display === "(blank)"
                                  ? {
                                      fontStyle: "italic",
                                      color: "text.secondary"
                                    }
                                  : {}
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Stack>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: "divider" }}>
          <Button onClick={() => setFixedValuesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadExcelDialogEnhanced;
